import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { FullscreenLoader } from '@hrlogics/react-components';

import { appRoutes } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import {
  useEmployeeRegistrationMutation,
  useGetEmployeeDataOnEmployeeSignupQuery,
  useGetEmploymentDataOnEmployeeSignupLazyQuery,
  useGetEmploymentDataOnEmployeeSignupQuery,
} from 'src/api';
import { EmployeeDataEmploymentRecordCard } from 'src/components/employee/EmployeeDataEmploymentRecordCard';
import { PositionDetailsContent } from 'src/components/position';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useAppAuth, useAuthSignupConsumer } from 'src/hooks/auth';
import { useScreenInfo } from 'src/hooks/common';
import { useModal, useModalV2 } from 'src/hooks/shared';
import { CreateDataDisputeOnEmployeeSignupModal } from 'src/modals/dataDispute';
import { PositionDetailsModalArgs } from 'src/modals/position';

import { prepareFormDataConsumer } from '../utils';

export const ConsumerReviewDataForm: React.FC = () => {
  const navigate = useNavigate();

  const { formData, employeeSignupToken } = useAuthSignupConsumer();
  const { setAuthState } = useAppAuth();

  const { isMobile } = useScreenInfo();
  const { openModal } = useModal();
  const { openModal: openModalV2 } = useModalV2();
  const { createApolloContext } = useNotification();

  const [selectedCompanyId, setSelectedCompanyId] = React.useState<Nullable<string>>(null);

  const [signUpConsumer, { loading: isSigningUp }] = useEmployeeRegistrationMutation({
    context: createApolloContext(),
  });

  const { data: employeeData, loading: employeeDataLoading } =
    useGetEmployeeDataOnEmployeeSignupQuery({
      variables: {
        employeeSignupToken: employeeSignupToken || '',
      },
      skip: !employeeSignupToken,
    });

  const { data: employmentData, loading: employmentDataLoading } =
    useGetEmploymentDataOnEmployeeSignupQuery({
      variables: {
        employeeSignupToken: employeeSignupToken || '',
        companyId: selectedCompanyId || '',
      },
      skip: !employeeSignupToken || !selectedCompanyId,
    });
  const [getEmploymentData, { loading: employmentDataLazyLoading }] =
    useGetEmploymentDataOnEmployeeSignupLazyQuery();

  const handleSubmit = React.useCallback(async () => {
    if (!employeeSignupToken) return;
    try {
      const response = await signUpConsumer({
        variables: prepareFormDataConsumer(formData, employeeSignupToken),
      });
      const authData = response.data?.userRegistrationEmployee;
      await setAuthState({
        bearerToken: String(authData?.idToken),
        accessToken: authData?.accessToken ?? undefined,
      });
      navigate(appRoutes.auth.setupMfa);
    } catch (error) {
      console.error(error);
    }
  }, [signUpConsumer, formData, employeeSignupToken, setAuthState, navigate]);

  const handleDataDispute = React.useCallback(
    (companyId: string) => {
      if (!employeeSignupToken) return;

      openModalV2(CreateDataDisputeOnEmployeeSignupModal, {
        id: ModalId.CreateDataDisputeOnEmployeeSignup,
        employeeSignupToken,
        companyId,
        userData: {
          email: String(formData.email ?? ''),
          firstName: String(formData.firstName ?? ''),
          lastName: String(formData.lastName ?? ''),
        },
      });
    },
    [openModalV2, employeeSignupToken, formData],
  );

  const handleChangeView = React.useCallback(
    async (companyId: string) => {
      if (isMobile) {
        const { data: employmentData } = await getEmploymentData({
          variables: { employeeSignupToken: employeeSignupToken || '', companyId },
        });
        openModal(ModalId.PositionDetails, {
          employmentData: employmentData?.getEmploymentDataOnEmployeeSignup,
          onDataDispute: handleDataDispute,
        } satisfies PositionDetailsModalArgs);
        return;
      }
      setSelectedCompanyId(companyId);
    },
    [isMobile, getEmploymentData, openModal, employeeSignupToken, handleDataDispute],
  );

  if (employeeDataLoading || employmentDataLazyLoading) {
    return <FullscreenLoader />;
  }

  if (selectedCompanyId) {
    return (
      <PositionDetailsContent
        employmentData={employmentData?.getEmploymentDataOnEmployeeSignup}
        loading={employmentDataLoading}
        onBack={() => setSelectedCompanyId(null)}
        onDataDispute={handleDataDispute}
        isWatermarked
      />
    );
  }

  return (
    <Box>
      <Typography variant="body2" fontSize="24px">
        Review my data
      </Typography>
      <Typography variant="subtitle1" fontSize="16px" mt={1} color="lightslategrey">
        Read following information and confirm if it&apos;s correct
      </Typography>

      <Grid container spacing={2} mt={2}>
        {employeeData?.getEmployeeDataOnEmployeeSignup.employmentRecords.map(employmentRecord => (
          <Grid key={employmentRecord.companyId} size={12}>
            <EmployeeDataEmploymentRecordCard
              key={employmentRecord.companyId}
              employmentRecord={employmentRecord}
              onSeeMore={handleChangeView}
              loading={employmentDataLoading}
              hideEmployerLiveIndicator
            />
          </Grid>
        ))}

        <Grid size={12} textAlign="center">
          <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={() => handleSubmit()}
            disabled={
              isSigningUp ||
              !employeeData?.getEmployeeDataOnEmployeeSignup.employmentRecords ||
              employeeData.getEmployeeDataOnEmployeeSignup.employmentRecords.length === 0
            }
            loading={isSigningUp || employmentDataLoading}
          >
            Go to dashboard
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
